import { useState, useEffect } from "react";
import { X, Calendar, Clock, User, Building, DollarSign, CheckCircle, XCircle, Loader2, AlertCircle } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Card, CardContent } from "../../../../components/ui/card";
import { useAuth } from "../../../../context/AuthContext";
import Swal from "sweetalert2";
import {
  obtenerReservasZonaComun,
  aprobarReservaZonaComun,
  noAprobarReservaZonaComun,
  ReservaZonaComun,
} from "../../../../api/reservaZonaComunService";

interface DetailReservaModalProps {
  isOpen: boolean;
  reservaCodigo: string;
  onClose: (shouldReload?: boolean) => void;
}

export default function DetailReservaModal({
  isOpen,
  reservaCodigo,
  onClose,
}: DetailReservaModalProps) {
  const { session } = useAuth();
  const [reserva, setReserva] = useState<ReservaZonaComun | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const rolCodigo = (session?.rol?.id || "").toUpperCase();
  const isAdmin = rolCodigo === "AS" || rolCodigo === "AS-ADMINSOCIO" || rolCodigo === "AD" || rolCodigo === "AD-ADMINISTRADOR";
  const canApprove = isAdmin && reserva?.rzc_estatus === "P";

  useEffect(() => {
    if (isOpen && reservaCodigo) {
      loadReservaDetail();
    } else {
      setReserva(null);
    }
  }, [isOpen, reservaCodigo]);

  const loadReservaDetail = async () => {
    if (!reservaCodigo || !session?.unidad?.id) return;

    try {
      setIsLoading(true);

      const payload = {
        "unidad residencial codigo": session.unidad.id,
        "zona comun codigo": "",
        "apartamento codigo": "",
        "fecha (dd/mm/aaaa)": "",
        "reserva zona comun codigo": reservaCodigo,
      };

      const response = await obtenerReservasZonaComun(payload);

      if (response.code === "1" && response.object_ && response.object_.length > 0) {
        setReserva(response.object_[0]);
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response.message || "No se pudo cargar la información de la reserva",
          confirmButtonColor: "#d33",
          didOpen: () => {
            const container = document.querySelector('.swal2-container') as HTMLElement;
            const backdrop = document.querySelector('.swal2-backdrop-show') as HTMLElement;
            if (container) container.style.zIndex = '10050';
            if (backdrop) backdrop.style.zIndex = '10049';
          },
        });
        onClose();
      }
    } catch (error) {
      console.error("Error al cargar detalle de reserva:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error al cargar la información de la reserva",
        confirmButtonColor: "#d33",
        didOpen: () => {
          const container = document.querySelector('.swal2-container') as HTMLElement;
          const backdrop = document.querySelector('.swal2-backdrop-show') as HTMLElement;
          if (container) container.style.zIndex = '10050';
          if (backdrop) backdrop.style.zIndex = '10049';
        },
      });
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  const handleAprobar = async () => {
    if (!reserva) return;

    const result = await Swal.fire({
      title: "¿Aprobar reserva?",
      text: "¿Está seguro de que desea aprobar esta reserva?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3d5a40",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, aprobar",
      cancelButtonText: "Cancelar",
      customClass: {
        container: 'swal2-container-custom',
        popup: 'swal2-popup-custom'
      },
      didOpen: () => {
        const container = document.querySelector('.swal2-container') as HTMLElement;
        const backdrop = document.querySelector('.swal2-backdrop-show') as HTMLElement;
        if (container) container.style.zIndex = '10050';
        if (backdrop) backdrop.style.zIndex = '10049';
      },
    });

    if (!result.isConfirmed) return;

    try {
      setIsProcessing(true);

      const response = await aprobarReservaZonaComun({
        reserva_zona_comun_codigo: reserva.rzc_codigo,
      });

      if (response.code === "1" || response.code === 1) {
        Swal.fire({
          icon: "success",
          title: "Reserva aprobada",
          text: response.message || "La reserva ha sido aprobada exitosamente",
          confirmButtonColor: "#3d5a40",
          timer: 2000,
          timerProgressBar: true,
          didOpen: () => {
            const container = document.querySelector('.swal2-container') as HTMLElement;
            const backdrop = document.querySelector('.swal2-backdrop-show') as HTMLElement;
            if (container) container.style.zIndex = '10050';
            if (backdrop) backdrop.style.zIndex = '10049';
          },
        });
        onClose(true);
      } else {
        throw new Error(response.message || "Error al aprobar la reserva");
      }
    } catch (error) {
      console.error("Error al aprobar reserva:", error);
      const errorMessage = error instanceof Error ? error.message : "Error al aprobar la reserva";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
        confirmButtonColor: "#d33",
        didOpen: () => {
          const container = document.querySelector('.swal2-container') as HTMLElement;
          const backdrop = document.querySelector('.swal2-backdrop-show') as HTMLElement;
          if (container) container.style.zIndex = '10050';
          if (backdrop) backdrop.style.zIndex = '10049';
        },
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleNoAprobar = async () => {
    if (!reserva) return;

    const result = await Swal.fire({
      title: "¿No aprobar reserva?",
      text: "¿Está seguro de que desea rechazar esta reserva?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sí, rechazar",
      cancelButtonText: "Cancelar",
      customClass: {
        container: 'swal2-container-custom',
        popup: 'swal2-popup-custom'
      },
      didOpen: () => {
        const container = document.querySelector('.swal2-container') as HTMLElement;
        const backdrop = document.querySelector('.swal2-backdrop-show') as HTMLElement;
        if (container) container.style.zIndex = '10050';
        if (backdrop) backdrop.style.zIndex = '10049';
      },
    });

    if (!result.isConfirmed) return;

    try {
      setIsProcessing(true);

      const response = await noAprobarReservaZonaComun({
        reserva_zona_comun_codigo: reserva.rzc_codigo,
      });

      if (response.code === "1" || response.code === 1) {
        Swal.fire({
          icon: "success",
          title: "Reserva rechazada",
          text: response.message || "La reserva ha sido rechazada exitosamente",
          confirmButtonColor: "#3d5a40",
          timer: 2000,
          timerProgressBar: true,
          didOpen: () => {
            const container = document.querySelector('.swal2-container') as HTMLElement;
            const backdrop = document.querySelector('.swal2-backdrop-show') as HTMLElement;
            if (container) container.style.zIndex = '10050';
            if (backdrop) backdrop.style.zIndex = '10049';
          },
        });
        onClose(true);
      } else {
        throw new Error(response.message || "Error al rechazar la reserva");
      }
    } catch (error) {
      console.error("Error al rechazar reserva:", error);
      const errorMessage = error instanceof Error ? error.message : "Error al rechazar la reserva";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
        confirmButtonColor: "#d33",
        didOpen: () => {
          const container = document.querySelector('.swal2-container') as HTMLElement;
          const backdrop = document.querySelector('.swal2-backdrop-show') as HTMLElement;
          if (container) container.style.zIndex = '10050';
          if (backdrop) backdrop.style.zIndex = '10049';
        },
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "Sin fecha";
    try {
      if (dateString.includes("/")) {
        const parts = dateString.split("/");
        if (parts.length === 3 && parts[0].length === 4) {
          return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
      }
      return dateString;
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (estatus: string) => {
    const estatusUpper = (estatus || "").toUpperCase();
    if (estatusUpper === "P" || estatusUpper.includes("PENDIENTE")) {
      return "bg-yellow-100 text-yellow-800 border border-yellow-300";
    } else if (estatusUpper === "S" || estatusUpper.includes("SEPARADA") || estatusUpper.includes("APROBADA")) {
      return "bg-green-100 text-green-800 border border-green-300";
    } else if (estatusUpper === "N" || estatusUpper.includes("NO APROBADA")) {
      return "bg-red-100 text-red-800 border border-red-300";
    }
    return "bg-gray-100 text-gray-800 border border-gray-300";
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isProcessing) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="sticky top-0 bg-gradient-to-r from-military-500 to-military-600 text-white px-6 py-4 rounded-t-xl flex items-center justify-between z-10">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Calendar size={28} />
            Detalle de Reserva
          </h2>
          <button
            onClick={() => onClose()}
            className="text-white hover:bg-white/20 rounded-full p-1 transition"
            disabled={isProcessing}
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-12 w-12 animate-spin text-military-600 mb-4" />
              <p className="text-gray-600">Cargando información de la reserva...</p>
            </div>
          ) : reserva ? (
            <div className="space-y-4">
              {/* Imagen de la zona común */}
              {reserva.rzc_ruta_foto_principal && (
                <div className="w-full h-64 sm:h-80 rounded-lg overflow-hidden bg-gray-200">
                  <img
                    src={reserva.rzc_ruta_foto_principal}
                    alt={reserva.rzc_zona_comun_nombre}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                </div>
              )}

              <Card className="border-2 border-gray-200">
                <CardContent className="p-4 sm:p-6 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {/* 1. Código */}
                    <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                      <p className="text-xs font-semibold text-gray-600 uppercase mb-1">Código</p>
                      <p className="text-base sm:text-lg font-bold text-gray-900 font-mono break-all">{reserva.rzc_codigo}</p>
                    </div>

                    {/* 2. Zona común */}
                    <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                      <p className="text-xs font-semibold text-gray-600 uppercase mb-1 flex items-center gap-2">
                        <Building size={14} />
                        Zona Común
                      </p>
                      <p className="text-base sm:text-lg font-semibold text-gray-900 break-words">{reserva.rzc_zona_comun_nombre}</p>
                    </div>

                    {/* 3. Estatus */}
                    <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                      <p className="text-xs font-semibold text-gray-600 uppercase mb-1">Estatus</p>
                      <span className={`inline-block px-3 py-1.5 rounded-full text-xs sm:text-sm font-bold ${getStatusColor(reserva.rzc_estatus)}`}>
                        {reserva.rzc_estatus_nombre}
                      </span>
                    </div>

                    {/* 4. Fecha */}
                    <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                      <p className="text-xs font-semibold text-gray-600 uppercase mb-1 flex items-center gap-2">
                        <Calendar size={14} />
                        Fecha
                      </p>
                      <p className="text-base sm:text-lg font-semibold text-gray-900">{formatDate(reserva.rzc_fecha_separacion)}</p>
                    </div>

                    {/* 5. Día */}
                    <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                      <p className="text-xs font-semibold text-gray-600 uppercase mb-1">Día</p>
                      <p className="text-base sm:text-lg font-semibold text-gray-900">{reserva.rzc_dia_reservado_nombre}</p>
                    </div>

                    {/* 6. Hora inicial */}
                    <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                      <p className="text-xs font-semibold text-gray-600 uppercase mb-1 flex items-center gap-2">
                        <Clock size={14} />
                        Hora Inicial
                      </p>
                      <p className="text-base sm:text-lg font-semibold text-gray-900">{reserva.rzc_hora_inicio_reservada}</p>
                    </div>

                    {/* 7. Hora finalización */}
                    <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                      <p className="text-xs font-semibold text-gray-600 uppercase mb-1 flex items-center gap-2">
                        <Clock size={14} />
                        Hora Finalización
                      </p>
                      <p className="text-base sm:text-lg font-semibold text-gray-900">{reserva.rzc_hora_final_reservada}</p>
                    </div>

                    {/* 8. Reservado por */}
                    <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                      <p className="text-xs font-semibold text-gray-600 uppercase mb-1 flex items-center gap-2">
                        <User size={14} />
                        Reservado por
                      </p>
                      <p className="text-base sm:text-lg font-semibold text-gray-900 break-words">{reserva.rzc_nombre_de_reservador}</p>
                    </div>

                    {/* 9. Apartamento */}
                    <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                      <p className="text-xs font-semibold text-gray-600 uppercase mb-1 flex items-center gap-2">
                        <Building size={14} />
                        Apartamento
                      </p>
                      <p className="text-base sm:text-lg font-semibold text-gray-900 break-words">{reserva.rzc_apartamento_nombre}</p>
                    </div>

                    {/* 10. Valor */}
                    <div className="bg-gray-50 rounded-lg p-3 sm:p-4 sm:col-span-2">
                      <p className="text-xs font-semibold text-gray-600 uppercase mb-1 flex items-center gap-2">
                        <DollarSign size={14} />
                        Valor
                      </p>
                      <p className="text-xl sm:text-2xl font-bold text-green-600">
                        ${reserva.rzc_valor.toLocaleString('es-CO')}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle className="h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-600">No se pudo cargar la información de la reserva</p>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t pt-4 px-6 pb-6">
          <div className="flex gap-4 justify-end">
            {canApprove && (
              <>
                <Button
                  onClick={handleAprobar}
                  disabled={isProcessing}
                  className="bg-green-600 hover:bg-green-700 text-white shadow-lg font-semibold flex items-center gap-2"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Procesando...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={18} />
                      Aprobar
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleNoAprobar}
                  disabled={isProcessing}
                  className="bg-red-600 hover:bg-red-700 text-white shadow-lg font-semibold flex items-center gap-2"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Procesando...
                    </>
                  ) : (
                    <>
                      <XCircle size={18} />
                      No Aprobar
                    </>
                  )}
                </Button>
              </>
            )}
            <Button
              onClick={() => onClose()}
              variant="outline"
              disabled={isProcessing}
              className="border-2 hover:bg-gray-50"
            >
              Cerrar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
